import { PasskeyRegistrationOptionsResponse, PasskeyAuthenticationOptionsResponse } from '@apis/model';
import { ResponseCode, PasskeyAuthenticateResponse } from '@ul/api';
import type { RegistrationResponseJSON, AuthenticationResponseJSON } from '@simplewebauthn/browser';
import {
    PasskeyRegistrationPlatformResponse,
    PasskeyAuthenticationPlatformResponse,
    PasskeyRegistrationExitState,
} from '@ul/common';
import * as React from 'react';
import { PasskeyTriggerSource } from '@services/hadoopMessage/Messenger';

export type PasskeyRegistrationState =
    | 'idle'
    | 'initiating'
    | 'need_otp'
    | 'retry_after_otp'
    | 'platform_register'
    | 'completing'
    | 'success'
    | 'error';

export type WalletPasskeyContext = {
    mfaOperationCode?: string;
    transactionId?: string;
};

export type PasskeyContext = {
    // State
    registrationState: PasskeyRegistrationState;
    registrationTriggerSource: PasskeyTriggerSource;
    isLoading: boolean;
    error: string | null;
    isSupported: boolean; // Whether passkeys are supported in this environment
    isPasskeyAutofillSupported: boolean; // Whether passkey autofill (conditional UI) is supported
    // Methods
    getPasskeyRegistrationOptions: () => Promise<PasskeyRegistrationOptionsResponse>;
    performPasskeyRegistration: (
        goToVerifyOtp?: () => void,
        goToPasskeyManagement?: () => void
    ) => Promise<PasskeyRegistrationOptionsResponse>;
    getPasskeyAuthenticationOptions: () => Promise<PasskeyAuthenticationOptionsResponse>;
    requestAuthenticationOnPlatform: (
        options: any,
        useConditionalMediation?: boolean
    ) => Promise<PasskeyAuthenticationPlatformResponse>;
    authenticateWithPasskey: (
        platformResponse: AuthenticationResponseJSON,
        walletContext?: WalletPasskeyContext
    ) => Promise<PasskeyAuthenticateResponse>;
    requestRegistrationOnPlatform: (options: any) => Promise<PasskeyRegistrationPlatformResponse>;
    registerPasskey: (platformResponse: RegistrationResponseJSON, goToVerifyOtp?: () => void) => Promise<void>;
    resetRegistration: () => void;
    setRegistrationState: (state: PasskeyRegistrationState) => void;
    handleBackButtonClick: (goToPasskeyManagement?: () => void) => Promise<void>;
    checkAutofillSupportOnPlatform: () => Promise<boolean>; // Check if conditional UI (autofill) is supported
    onPasskeyRegistrationExit: (exitState: PasskeyRegistrationExitState) => void;
    abortPasskeyRequestOnPlatform: () => void;
    performPasskeyAuthentication: (
        onSuccess: (responseCode: ResponseCode) => void,
        useAutofill?: boolean,
        isInputEmailFlow?: boolean,
        onCancelCallback?: (checkPasskey: boolean) => void,
        walletContext?: WalletPasskeyContext
    ) => Promise<void>;
    updateLocalPasskeyPreference: (skipPasskey?: boolean) => void;
    setRegistrationTriggerSource: (source: PasskeyTriggerSource) => void;
};

export const defaultPasskeyContext: PasskeyContext = {
    registrationState: 'idle',
    registrationTriggerSource: PasskeyTriggerSource.LOGIN,
    isLoading: false,
    error: null,
    isSupported: false,
    isPasskeyAutofillSupported: false,
    getPasskeyRegistrationOptions: async () => {
        return {} as PasskeyRegistrationOptionsResponse;
    },
    performPasskeyRegistration: async () => {
        return {} as PasskeyRegistrationOptionsResponse;
    },
    getPasskeyAuthenticationOptions: async () => {
        return {} as PasskeyAuthenticationOptionsResponse;
    },
    requestAuthenticationOnPlatform: async () => {
        return null;
    },
    authenticateWithPasskey: async () => {
        return {} as PasskeyAuthenticateResponse;
    },
    requestRegistrationOnPlatform: async () => {
        return null;
    },
    registerPasskey: async () => {},
    resetRegistration: () => {},
    setRegistrationState: () => {},
    handleBackButtonClick: async () => {},
    checkAutofillSupportOnPlatform: async () => false,
    onPasskeyRegistrationExit: (exitState: PasskeyRegistrationExitState) => {},
    abortPasskeyRequestOnPlatform: () => {},
    performPasskeyAuthentication: async () => {},
    updateLocalPasskeyPreference: () => {},
    setRegistrationTriggerSource: () => {},
};

export const PasskeyContext = React.createContext<PasskeyContext>(defaultPasskeyContext);
PasskeyContext.displayName = 'PasskeyContext';
